'use client';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Users,
  ShoppingBag,
  IndianRupee,
  MapPin,
  BarChart3,
} from 'lucide-react';

/**
 * CSS-rendered product dashboard. No images — renders instantly.
 * The single most important visual on the landing page.
 */
export default function DashboardMockup() {
  return (
    <div
      role="img"
      aria-label="Supe.Market Leadership Console showing the Briefing dashboard with revenue metrics, alerts, and team health"
      className="w-full select-none"
    >
      {/* Browser chrome */}
      <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-2xl">
        {/* Title bar */}
        <div className="flex items-center gap-2 border-b border-border/60 bg-[hsl(35,12%,96%)] px-4 py-2.5">
          <div className="flex gap-1.5" aria-hidden="true">
            <span className="h-2.5 w-2.5 rounded-full bg-[hsl(0,70%,65%)]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[hsl(40,85%,60%)]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[hsl(140,55%,52%)]" />
          </div>
          <div className="ml-3 flex-1">
            <div className="mx-auto max-w-xs rounded-md bg-white/80 px-3 py-1 text-center text-[10px] text-muted-foreground">
              app.supe.market
            </div>
          </div>
        </div>

        {/* App content */}
        <div className="flex" aria-hidden="true">
          {/* Mini sidebar */}
          <div className="hidden w-14 shrink-0 border-r border-border/40 bg-[hsl(24,15%,7%)] py-4 sm:block">
            <div className="mb-6 flex justify-center">
              <div className="h-6 w-6 rounded-md bg-accent/80" />
            </div>
            <div className="flex flex-col items-center gap-3">
              {[BarChart3, MapPin, ShoppingBag, Users].map((Icon, i) => (
                <div
                  key={i}
                  className={`flex h-8 w-8 items-center justify-center rounded-lg ${i === 0 ? 'bg-white/10 text-white' : 'text-white/30'}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                </div>
              ))}
            </div>
          </div>

          {/* Main content */}
          <div className="min-h-[280px] flex-1 bg-[hsl(40,30%,98%)] p-3 sm:min-h-[340px] sm:p-5">
            {/* Greeting */}
            <div className="mb-4">
              <p className="text-[11px] font-semibold text-foreground sm:text-xs">
                Good morning, Rajesh
              </p>
              <p className="text-[9px] text-muted-foreground sm:text-[10px]">
                March 2026 &middot; Day 18 of 31
              </p>
            </div>

            {/* Alert strip */}
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200/60 bg-amber-50/80 px-3 py-1.5">
              <AlertTriangle className="h-3 w-3 shrink-0 text-amber-600" />
              <p className="truncate text-[9px] font-medium text-amber-900 sm:text-[10px]">
                Mohammed Khan coverage 65.5% — below 80% threshold for 4
                consecutive months
              </p>
            </div>

            {/* Metric cards */}
            <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
              <MetricCard
                label="Revenue MTD"
                value="₹2.4Cr"
                change="+12.3%"
                positive
                icon={IndianRupee}
              />
              <MetricCard
                label="Collection"
                value="87.2%"
                change="+3.1%"
                positive
                icon={TrendingUp}
              />
              <MetricCard
                label="Coverage"
                value="78.4%"
                change="-2.8%"
                positive={false}
                icon={MapPin}
              />
              <MetricCard
                label="Active Retailers"
                value="8,432"
                change="+156"
                positive
                icon={Users}
              />
            </div>

            {/* Team health bar + mini chart */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
              <div className="rounded-lg border border-border/40 bg-white p-3 sm:col-span-3">
                <p className="mb-2 text-[9px] font-semibold text-foreground sm:text-[10px]">
                  Team Health
                </p>
                <div className="mb-1.5 flex h-3 overflow-hidden rounded-full">
                  <div className="bg-emerald-500" style={{ width: '58%' }} />
                  <div className="bg-amber-400" style={{ width: '27%' }} />
                  <div className="bg-red-400" style={{ width: '15%' }} />
                </div>
                <div className="flex gap-3 text-[8px] text-muted-foreground sm:text-[9px]">
                  <span className="flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    On Track 58%
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                    Attention 27%
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                    Critical 15%
                  </span>
                </div>
              </div>

              <div className="hidden rounded-lg border border-border/40 bg-white p-3 sm:col-span-2 sm:block">
                <p className="mb-2 text-[10px] font-semibold text-foreground">
                  Revenue Trend
                </p>
                <MiniChart />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  change,
  positive,
  icon: Icon,
}: {
  label: string;
  value: string;
  change: string;
  positive: boolean;
  icon: React.ElementType;
}) {
  return (
    <div className="rounded-lg border border-border/40 bg-white p-2.5 sm:p-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[8px] font-medium text-muted-foreground sm:text-[9px]">
          {label}
        </span>
        <Icon className="h-3 w-3 text-muted-foreground/40" />
      </div>
      <p className="text-sm font-bold text-foreground sm:text-base">{value}</p>
      <div className="mt-0.5 flex items-center gap-0.5">
        {positive ? (
          <TrendingUp className="h-2.5 w-2.5 text-emerald-600" />
        ) : (
          <TrendingDown className="h-2.5 w-2.5 text-red-500" />
        )}
        <span
          className={`text-[8px] font-semibold sm:text-[9px] ${positive ? 'text-emerald-600' : 'text-red-500'}`}
        >
          {change}
        </span>
      </div>
    </div>
  );
}

function MiniChart() {
  const points = [30, 45, 35, 55, 50, 65, 60, 75, 70, 85, 78, 92];
  const w = 160;
  const h = 50;
  const maxVal = Math.max(...points);
  const coords = points.map((p, i) => ({
    x: (i / (points.length - 1)) * w,
    y: h - (p / maxVal) * h,
  }));
  const pathD = coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`)
    .join(' ');
  const areaD = `${pathD} L${w},${h} L0,${h} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-10 w-full" aria-hidden="true">
      <defs>
        <linearGradient id="mchart" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(30 95% 52%)" stopOpacity="0.18" />
          <stop offset="100%" stopColor="hsl(30 95% 52%)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaD} fill="url(#mchart)" />
      <path
        d={pathD}
        fill="none"
        stroke="hsl(30 95% 52%)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
